import { combineReducers } from 'redux';
import { configureStore as configureStoreToolkit } from '@reduxjs/toolkit';
import { createListenerMiddleware } from '../store/listenerMiddleware/listenerMiddleware';
import { AuthFetch } from './domain/AuthFetch';
import { Cookies } from './appTypings';
import { rootApi } from '../store/rootApi';
import { searchResultsSlice } from '../store/search/slices';
import { playerViewFetchSuccessReducer } from '../store/player/reducers';
import { promotionsShownReducer } from '../store/promotion/reducers';
import { geoLocationSuccessReducer } from '../store/geoLocation/reducers';
import episodesViewsReducer from '../store/episodes/slices';
import { settingsViewReducer } from '../store/settings/reducers';
import { Features } from './contexts/AppFeaturesContext/AppFeaturesContext';

const reducers = {
  playerViews: playerViewFetchSuccessReducer,
  searchResults: searchResultsSlice.reducer,
  settingsView: settingsViewReducer,
  currentAddress: geoLocationSuccessReducer,
  promotions: promotionsShownReducer,
  [rootApi.reducerPath]: rootApi.reducer,
  episodesViews: episodesViewsReducer,
};

const serializableCheck = {
  ignoredPaths: [rootApi.reducerPath],
  ignoredActionPaths: ['meta', 'payload'],
};

const rootReducer = combineReducers(reducers);

type RootReducer = typeof rootReducer;

export default function configureStore(
  preloadedState: Partial<RootReducer>,
  extra: ExtraArguments
) {
  return configureStoreToolkit({
    preloadedState,
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: { extraArgument: extra },
        serializableCheck,
      }).concat([
        createListenerMiddleware(extra).middleware,
        rootApi.middleware,
      ]),
    enhancers: defaultEnhancers => defaultEnhancers,
    devTools: process.env.NODE_ENV !== 'production',
  });
}

export type ConfiguredStore = ReturnType<typeof configureStore>;
type StoreGetState = ConfiguredStore['getState'];
export type State = ReturnType<StoreGetState>;
export type Dispatch = ConfiguredStore['dispatch'];
export type Thunk<R> = (
  dispatch: Dispatch,
  getState: StoreGetState,
  extraArguments: ExtraArguments
) => R;

export interface ExtraArguments {
  authFetch: AuthFetch;
  appFeatures: Omit<Features, 'languageCode'>;
  serverCookies?: Cookies;
}

export type OptionalExtraArguments = {
  [K in keyof ExtraArguments]?: ExtraArguments[K];
};
